import classNames from "classnames/bind";
import { Product, RuntimeConfig, FeatureFlags } from "@/types/types";
import { ChangeEvent, useEffect, useState } from "react";
import { Accordion } from "@JohnLewisPartnership/jl-design-system/dist/components/accordion/Accordion";
import { AccordionItem } from "@JohnLewisPartnership/jl-design-system/dist/components/accordion/accordion-item/AccordionItem";
import {
  PrimaryButton,
  UnderlinedButton,
} from "@JohnLewisPartnership/jl-design-system/dist/elements/button/Button";
import RadioButton from "@JohnLewisPartnership/jl-design-system/dist/components/radio-button/RadioButton";
import OptionList from "@JohnLewisPartnership/jl-design-system/dist/components/option/OptionList";
import { TextInput } from "@JohnLewisPartnership/jl-design-system/dist/components/text-input/TextInput";
import { Message } from "@JohnLewisPartnership/jl-design-system/dist/components/message/Message";
import { HorizontalRule } from "@/components/HorizontalRule/HorizontalRule";
import Price from "@JohnLewisPartnership/jl-design-system/dist/components/price/Price";
import { TickCircleFilled } from "@JohnLewisPartnership/jl-design-system/dist/components/icons/TickCircleFilled";
import { ErrorFilled } from "@JohnLewisPartnership/jl-design-system/dist/components/icons/ErrorFilled";

import styles from "../CurtainsForm/CurtainsForm.module.scss";
import {
  CURTAINS_DESCRIPTION,
  M2M_CURTAINS_DEFAULT_EYELET_COLOUR,
  M2M_CURTAINS_MIN_HEIGHT,
  M2M_CURTAINS_MIN_WIDTH,
  M2M_CURTAINS_DEFAULT_HEADING_TYPE,
  M2M_DEFAULT_LINING_WEIGHT,
} from "../../util/constants";
import { useVariant } from "@/hooks/useVariant/useVariant";
import {
  getM2MPriceWithBlackout,
  M2MError,
  M2MErrorType,
} from "../../util/getM2MPrice";
import { MadeToMeasureReviewModal } from "../MadeToMeasureReviewModal/MadeToMeasureReviewModal";
import { PriceError } from "../PriceError/PriceError";
import { formatM2MTitle } from "@/utils/product/formatM2MTitle";
import {
  LiningWeight,
  CurtainConfiguration,
  HeadingType,
} from "../../configurationTypes";
import {
  M2MHelpSection,
  MadeToMeasureHelpModal,
} from "../MadeToMeasureHelpModal/MadeToMeasureHelpModal";
import {
  getConfigFromCurtainsCookie,
  setCurtainsCookieFromConfig,
} from "../../util/madeToMeasureCookieHelper";

const cx = classNames.bind(styles);

type Props = {
  product: Product;
  runtimeConfig: RuntimeConfig;
  flags: FeatureFlags;
};

type MeasurementFieldProps = {
  value: string;
  valid: boolean;
  error: string;
};

type MeasurementFields = {
  width: MeasurementFieldProps;
  height: MeasurementFieldProps;
};

type RadioButtonProps = {
  value: string;
};

type HeadingRadioButtonProps = {
  value: HeadingType;
};

type LiningWeightRadioButtonProps = {
  value: LiningWeight;
};

type RadioButtons = {
  headingType: HeadingRadioButtonProps;
  curtainType: RadioButtonProps;
  liningWeight: LiningWeightRadioButtonProps;
  liningColour: RadioButtonProps;
  eyeletColour: RadioButtonProps;
};

export const CurtainsForm = ({ product, runtimeConfig, flags }: Props) => {
  const { variant } = useVariant(product);

  const priceFrom = { now: variant.price?.value };

  const [measurementFields, setMeasurementsFields] =
    useState<MeasurementFields>({
      width: { value: "", valid: false, error: "" },
      height: { value: "", valid: false, error: "" },
    });

  const [radioButtonValues, setRadioButtonValues] = useState<RadioButtons>({
    headingType: { value: M2M_CURTAINS_DEFAULT_HEADING_TYPE },
    curtainType: { value: "Pair" },
    liningWeight: { value: M2M_DEFAULT_LINING_WEIGHT },
    liningColour: { value: "Cream" },
    eyeletColour: { value: M2M_CURTAINS_DEFAULT_EYELET_COLOUR },
  });

  const [priceError, setPriceError] = useState<M2MError>({
    heading: "",
    body: "",
    details: undefined,
    type: "",
  });
  const [totalPrice, setTotalPrice] = useState("");
  const [blackoutPrice, setBlackoutPrice] = useState("");
  const [isReviewButtonEnabled, setIsReviewButtonEnabled] = useState(false);
  const [isPriceCalculating, setIsPriceCalculating] = useState(false);
  const [showDropWarning, setShowDropWarning] = useState(false);
  const [maxRailSize, setMaxRailSize] = useState("");
  const [maxDrop, setMaxDrop] = useState("");
  const [formUpdatedFromCookie, setFormUpdatedFromCookie] = useState(false);

  const eyelets = [
    { name: "Stainless Steel", altName: "stainlessSteel" },
    { name: "Antique Nickel", altName: "antiqueNickel" },
    { name: "Gunmetal", altName: "gunmetal" },
    { name: "Brass", altName: "brass" },
    { name: "Antique Brass", altName: "antiqueBrass" },
    { name: "Antique Copper", altName: "antiqueCopper" },
    { name: "Matt Black", altName: "mattBlack" },
  ];

  const getPreviewImageUrl = (suffix = "") => {
    return suffix
      ? variant?.images.alternatives.find((img) => img.endsWith(suffix))
      : "";
  };

  const [selectedPreviewImage, setSelectedPreviewImage] = useState(
    getPreviewImageUrl("WSPP"),
  );

  const [helpModal, updateHelpModal] = useState({
    open: false,
    visibleSection: { name: "", title: "" },
  });

  const helpSections = [
    { name: "curtains-heading-styles", title: "Heading Styles" },
    {
      name: "curtains-measurements-eyelet",
      title: "Measurements - Eyelet Curtains",
    },
    {
      name: "curtains-measurements-pleated",
      title: "Measurements - Pleated Curtains",
    },
    { name: "curtains-lining-options", title: "Lining Options" },
  ];

  const toggleHelpModal = (section: M2MHelpSection = { name: "", title: "" }) =>
    updateHelpModal({ open: !helpModal.open, visibleSection: section });

  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);

  const getFabric = () => ({
    variantId: variant.id,
    sku: variant.aliases.skuId,
    displayCode: variant.displayCode,
    productName: variant.title,
  });

  const getConfigFromFormData = (): CurtainConfiguration => {
    return {
      type: "Curtain",
      headingType: radioButtonValues.headingType.value,
      typeOfCurtain: radioButtonValues.curtainType.value,
      lengthOfTrackOrPole: parseFloat(measurementFields.width.value),
      eyeDrop: parseFloat(measurementFields.height.value),
      liningWeight: radioButtonValues.liningWeight.value,
      liningColour: radioButtonValues.liningColour.value,
      ...(radioButtonValues.headingType.value === HeadingType.Eyelet && {
        eyeletColour: radioButtonValues.eyeletColour.value,
      }),
    };
  };

  const setFormDataFromConfig = (config: CurtainConfiguration) => {
    const {
      headingType,
      typeOfCurtain,
      liningWeight,
      liningColour,
      eyeletColour,
      lengthOfTrackOrPole,
      eyeDrop,
    } = config;
    setRadioButtonValues({
      headingType: {
        value: headingType,
      },
      curtainType: {
        value: typeOfCurtain,
      },
      liningWeight: {
        value: liningWeight,
      },
      liningColour: {
        value: liningColour,
      },
      eyeletColour: {
        value: eyeletColour || M2M_CURTAINS_DEFAULT_EYELET_COLOUR,
      },
    });
    setMeasurementsFields({
      width: {
        value: lengthOfTrackOrPole ? lengthOfTrackOrPole.toString() : "",
        valid: lengthOfTrackOrPole > 0,
        error: "",
      },
      height: {
        value: eyeDrop ? eyeDrop.toString() : "",
        valid: eyeDrop > 0,
        error: "",
      },
    });

    setFormUpdatedFromCookie(true);
  };

  const isDecimalValueToMax2Places = (value: string) => {
    const regExDecimalCondition = /^[0-9]+(\.([0-9]{1,2})?)?$/;
    return regExDecimalCondition.test(value);
  };

  const checkAllMeasurementsValid = () => {
    const measurementsValid = Object.values(measurementFields).every(
      (field) => field.valid && !field.error,
    );
    const radioButtonsValid = Object.values(radioButtonValues).every(
      (field) => field.value !== "",
    );
    return measurementsValid && radioButtonsValid;
  };

  const getErrorMessage = (field: string, type: string, value: string) =>
    `This is below the ${type}imum ${field} of ${value} centimetres available for this fabric`;

  const formatDecimalValue = (value: string, name: string) => {
    if (!isDecimalValueToMax2Places(value)) {
      setMeasurementsFields({
        ...measurementFields,
        [name]: { value: parseFloat(value).toFixed(2) },
      });
      return false;
    }
    return true;
  };

  const getMinValueForField = (name: string) => {
    switch (name) {
      case "width":
        return M2M_CURTAINS_MIN_WIDTH;
      case "height":
        return M2M_CURTAINS_MIN_HEIGHT;
      default:
        return "0";
    }
  };

  const clearPriceError = () => {
    if (priceError?.type)
      setPriceError({ heading: "", body: "", type: "", details: undefined });
  };

  const updateMeasurementFields = (e: ChangeEvent) => {
    if (!(e.target instanceof HTMLInputElement)) return;

    const { name, value } = e.target;

    clearPriceError();

    // Restrict field values to 2 decimal places
    if (value.includes(".")) {
      const formatDecimal = formatDecimalValue(value, name);
      if (!formatDecimal) return;
    }

    if (name === "height" && value) {
      if (showDropWarning) setShowDropWarning(false);
    }

    const valueAsFloat = parseFloat(value);
    const minValue = getMinValueForField(name);
    let valid = valueAsFloat > 0 && valueAsFloat >= parseInt(minValue);

    let errorType = "",
      errorValue = "";

    if (!valid) {
      errorType = "min";
      errorValue = minValue;
    }

    if (valid && name === "width" && parseFloat(maxRailSize) > 0) {
      valid = valueAsFloat <= parseFloat(maxRailSize);
      errorType = "max";
      errorValue = maxRailSize;
    }

    if (valid && name === "height" && parseFloat(maxDrop) > 0) {
      valid = valueAsFloat <= parseFloat(maxDrop);
      errorType = "max";
      errorValue = maxDrop;
    }

    const errorMsg =
      !valid && value.length > 1
        ? getErrorMessage(name, errorType, errorValue)
        : "";

    const fields = {
      [name]: { value, valid, error: errorMsg },
    };

    setMeasurementsFields({ ...measurementFields, ...fields });
  };

  const updateRadioButton = (e: ChangeEvent) => {
    if (!(e.target instanceof HTMLInputElement)) return;

    const { name, value } = e.target;

    const fields = {
      [name]: { value },
    };

    const updatedFields = { ...radioButtonValues, ...fields };

    setRadioButtonValues(updatedFields);

    if (measurementFields.height.value) {
      if (
        name === "headingType" &&
        ((value === HeadingType.Eyelet &&
          radioButtonValues.headingType.value !== HeadingType.Eyelet) || // non-eyelet -> eyelet
          (value !== HeadingType.Eyelet &&
            radioButtonValues.headingType.value === HeadingType.Eyelet)) // eyelet -> non-eyelet
      ) {
        // clear height value and display warning when switching to, or from eyelet headingType
        setMeasurementsFields({
          ...measurementFields,
          height: { value: "", valid: false, error: "" },
        });
        if (!showDropWarning) setShowDropWarning(true);
        reset();
      }
    }
  };

  const reset = () => {
    if (isReviewButtonEnabled) setIsReviewButtonEnabled(false);
    if (totalPrice) setTotalPrice("");
    if (blackoutPrice) setBlackoutPrice("");
  };

  const updatePrice = async () => {
    setIsPriceCalculating(true);
    clearPriceError();
    const configuration = getConfigFromFormData();
    const { price, error } = await getM2MPriceWithBlackout(
      runtimeConfig.aceApiBaseUrl,
      "MadeToMeasureCurtain",
      getFabric(),
      1,
      configuration,
    );

    setIsPriceCalculating(false);

    if (price) {
      setTotalPrice(price.total);
      if (price.blackoutLiningPrice)
        setBlackoutPrice(price.blackoutLiningPrice);
      setIsReviewButtonEnabled(true);
    }
    if (error) {
      setPriceError(error);
      if (error?.details?.maxRailSize) {
        setMaxRailSize(error.details.maxRailSize);
        setMeasurementsFields({
          ...measurementFields,
          width: { value: "", valid: false, error: "" },
        });
      }
      if (error?.details?.maxDrop) {
        setMaxDrop(error.details.maxDrop);
        setMeasurementsFields({
          ...measurementFields,
          height: { value: "", valid: false, error: "" },
        });
      }
    }
  };

  const requestPrice = () => {
    if (checkAllMeasurementsValid()) {
      updatePrice();
    } else {
      reset();
    }
  };

  useEffect(() => {
    const configuration = getConfigFromCurtainsCookie();
    if (configuration) setFormDataFromConfig(configuration);
  }, []);

  useEffect(() => {
    const imageSuffixMap: { [key: string]: string } = {
      [HeadingType.PencilPleat]: "WSPP",
      [HeadingType.DoublePinch]: "WSDP",
      [HeadingType.TriplePleat]: "WSTP",
      [HeadingType.Eyelet]: "WSEL",
    };

    const previewImageUrl = getPreviewImageUrl(
      imageSuffixMap[radioButtonValues.headingType.value],
    );
    if (previewImageUrl) setSelectedPreviewImage(previewImageUrl);
  }, [radioButtonValues.headingType.value]);

  useEffect(() => {
    if (checkAllMeasurementsValid()) {
      const total = parseFloat(totalPrice.replace("£", ""));
      const blackout = parseFloat(blackoutPrice.replace("£", ""));

      setTotalPrice(
        `£${(radioButtonValues.liningWeight.value === LiningWeight.Standard ? total - blackout : total + blackout).toFixed(2)}`,
      );
    }
  }, [radioButtonValues.liningWeight.value]);

  useEffect(() => {
    if (checkAllMeasurementsValid()) {
      setCurtainsCookieFromConfig(getConfigFromFormData());
    }
  }, [
    radioButtonValues.headingType.value,
    radioButtonValues.curtainType.value,
    radioButtonValues.eyeletColour.value,
    radioButtonValues.liningColour.value,
    radioButtonValues.liningWeight.value,
    measurementFields.width.value,
    measurementFields.height.value,
  ]);

  useEffect(() => {
    if (checkAllMeasurementsValid()) {
      requestPrice();
    }
  }, [
    radioButtonValues.headingType.value,
    radioButtonValues.curtainType.value,
  ]);

  useEffect(() => {
    if (
      formUpdatedFromCookie &&
      radioButtonValues.headingType.value === HeadingType.PencilPleat &&
      radioButtonValues.curtainType.value === "Pair"
      // only need to request if matches default values
    ) {
      requestPrice();
      setFormUpdatedFromCookie(false);
    }
  }, [
    formUpdatedFromCookie,
    radioButtonValues.headingType.value,
    radioButtonValues.curtainType.value,
  ]);

  return (
    <div className={styles.CurtainsForm} data-testid="m2m-curtains-form">
      <h3 className={styles.CurtainsForm__title} data-testid="m2m-form-title">
        Create your curtain to get a price
      </h3>
      <div data-testid="m2m-form-description">
        <p>{CURTAINS_DESCRIPTION}</p>
      </div>
      <form
        className={styles.CurtainsForm__form}
        aria-label="Made to Measure Curtains form"
      >
        <div
          className={styles.CurtainsForm__accordionSection}
          data-testid="m2m-curtains-accordions-container"
        >
          <Accordion>
            <AccordionItem
              id="accordion-m2m-curtains-heading-style"
              header={
                <div className={styles.CurtainsForm__accordionTitle}>
                  1. Choose a heading style
                </div>
              }
              isOpen={true}
              aria-label="1. Choose a heading style"
            >
              <UnderlinedButton
                tag="a"
                type="button"
                onClick={() => toggleHelpModal(helpSections[0])}
                className={styles.CurtainsForm__help_link}
              >
                Learn about heading styles
              </UnderlinedButton>
              <div className={styles.OptionsListContainer}>
                <div className={styles.OptionsList}>
                  <div className={styles.OptionsList__radio}>
                    <img src="https://static.johnlewiscontent.com/v1/static/curtain-heading-pencilPleat" />
                    <RadioButton
                      id="pencil-pleat"
                      label={HeadingType.PencilPleat}
                      value={HeadingType.PencilPleat}
                      name="headingType"
                      checked={
                        radioButtonValues.headingType.value ===
                        HeadingType.PencilPleat
                      }
                      onChange={updateRadioButton}
                      isControlled
                    />
                  </div>
                  <div className={styles.OptionsList__radio}>
                    <img src="https://static.johnlewiscontent.com/v1/static/curtain-heading-doublePinchPleat" />
                    <RadioButton
                      id="double-pinch-pleat"
                      label={HeadingType.DoublePinch}
                      value={HeadingType.DoublePinch}
                      name="headingType"
                      checked={
                        radioButtonValues.headingType.value ===
                        HeadingType.DoublePinch
                      }
                      onChange={updateRadioButton}
                      isControlled
                    />
                  </div>
                  <div className={styles.OptionsList__radio}>
                    <img src="https://static.johnlewiscontent.com/v1/static/curtain-heading-triplePinchPleat" />
                    <RadioButton
                      id="triple-pinch-pleat"
                      label={HeadingType.TriplePleat}
                      value={HeadingType.TriplePleat}
                      name="headingType"
                      checked={
                        radioButtonValues.headingType.value ===
                        HeadingType.TriplePleat
                      }
                      onChange={updateRadioButton}
                      isControlled
                    />
                  </div>
                  <div className={styles.OptionsList__radio}>
                    <img src="https://static.johnlewiscontent.com/v1/static/curtain-heading-eyelet" />
                    <RadioButton
                      id="eyelet"
                      label={HeadingType.Eyelet}
                      value={HeadingType.Eyelet}
                      name="headingType"
                      checked={
                        radioButtonValues.headingType.value ===
                        HeadingType.Eyelet
                      }
                      onChange={updateRadioButton}
                      isControlled
                    />
                  </div>
                </div>
                {selectedPreviewImage && (
                  <div className={styles.OptionsList}>
                    <img
                      className={styles.OptionsList__preview}
                      src={selectedPreviewImage} // deepcode ignore javascript/DOMXSS
                      alt="Preview Heading Type"
                    />
                  </div>
                )}
              </div>
              {showDropWarning && (
                <Message
                  title="Check your drop measurement"
                  body={
                    <p>
                      Changing your heading style affects the drop of your
                      curtain. Follow our{" "}
                      <UnderlinedButton
                        tag="a"
                        onClick={() =>
                          toggleHelpModal(
                            helpSections[
                              radioButtonValues.headingType.value ===
                              HeadingType.Eyelet
                                ? 1
                                : 2
                            ],
                          )
                        }
                      >
                        {" "}
                        guide for measuring curtains
                      </UnderlinedButton>{" "}
                      and add your drop details again.
                    </p>
                  }
                  type="warning"
                />
              )}
              <div
                style={{
                  display:
                    radioButtonValues.headingType.value === HeadingType.Eyelet
                      ? "block"
                      : "none",
                }}
              >
                <HorizontalRule />
                <p className={styles.CurtainsForm__description}>
                  Choose your eyelet colour
                </p>
                <div className={styles.EyeletList}>
                  {eyelets.map((eyelet) => {
                    const { name, altName } = eyelet;
                    return (
                      <CurtainEyelet
                        name={name}
                        altName={altName}
                        value={radioButtonValues.eyeletColour.value}
                        onChange={updateRadioButton}
                        key={name}
                      />
                    );
                  })}
                </div>
              </div>
            </AccordionItem>

            <AccordionItem
              id="accordion-m2m-curtains-measurements"
              header={
                <div className={styles.CurtainsForm__accordionTitle}>
                  2. Enter your measurements
                </div>
              }
              isOpen={false}
              aria-label="2. Enter your measurements"
            >
              <div className={styles.CurtainsForm__chainSection}>
                <div data-testid="m2m-chain-description">
                  <p>Choose a pair or single curtain</p>
                </div>
                <div data-testid="m2m-chain-options">
                  <OptionList name="curtainType" isButtonList={false}>
                    <RadioButton
                      id="pair-curtain"
                      label="Pair"
                      value="Pair"
                      checked={radioButtonValues.curtainType.value === "Pair"}
                      onChange={updateRadioButton}
                      isControlled
                    />
                    <RadioButton
                      id="single-curtain"
                      label="Single"
                      value="Single"
                      checked={radioButtonValues.curtainType.value === "Single"}
                      onChange={updateRadioButton}
                      isControlled
                    />
                  </OptionList>
                </div>
              </div>
              <HorizontalRule />
              <div className={styles.CurtainsForm__chainSection}>
                <div data-testid="m2m-add-measurements">
                  <p className={styles.CurtainsForm__help_link}>
                    Enter your measurements in centimetres (cm)
                  </p>
                </div>
                <UnderlinedButton
                  tag="a"
                  type="button"
                  onClick={() =>
                    toggleHelpModal(
                      helpSections[
                        radioButtonValues.headingType.value ===
                        HeadingType.Eyelet
                          ? 1
                          : 2
                      ],
                    )
                  }
                  className={styles.CurtainsForm__help_link}
                >
                  Learn how to measure correctly
                </UnderlinedButton>
                <div
                  className={styles.CurtainsForm__inputContainer}
                  data-testid="m2m-input-container:width"
                >
                  <label data-testid="m2m-input-label:width">
                    Width of track or pole
                  </label>
                  <div className={styles.CurtainsForm__inputMeasurement}>
                    <TextInput
                      aria-label="width in centimeters"
                      id="width"
                      name="width"
                      type="number"
                      placeholderText="e.g. 150.5"
                      labelText="Width of track or pole"
                      showLabel={false}
                      required={true}
                      readOnly={false}
                      value={measurementFields.width.value}
                      className={cx(styles.CurtainsForm__inputField, {
                        InputInvalid: measurementFields.width.error,
                      })}
                      min={M2M_CURTAINS_MIN_WIDTH}
                      max={maxRailSize}
                      data-testid="m2m-input:width"
                      onChange={updateMeasurementFields}
                      onBlur={requestPrice}
                    />
                    <span
                      className={styles.CurtainsForm__unit}
                      aria-hidden="true"
                    >
                      cm
                    </span>
                    {measurementFields.width.valid && (
                      <TickCircleFilled
                        size="large"
                        className={styles.tickIcon}
                      />
                    )}
                    {measurementFields.width.error && (
                      <ErrorFilled size="large" className={styles.errorIcon} />
                    )}
                  </div>
                  {priceError?.type === M2MErrorType.WIDTH && (
                    <PriceError
                      errorHeading={priceError.heading}
                      errorBody={priceError.body}
                    />
                  )}
                  {measurementFields.width.error && (
                    <div data-testid="m2m-curtains-input-error:width">
                      <span className={styles.CurtainsForm__inputFieldError}>
                        {measurementFields.width.error}
                      </span>
                    </div>
                  )}
                  <p
                    className={styles.CurtainsForm__instruction}
                    data-testid="m2m-curtains-input-description:width"
                  >
                    Minimum width is {M2M_CURTAINS_MIN_WIDTH}cm
                  </p>
                </div>

                <div
                  className={styles.CurtainsForm__inputContainer}
                  data-testid="m2m-curtains-input-container:height"
                >
                  <label data-testid="m2m-curtains-input-label:height">
                    Drop
                  </label>
                  <div className={styles.CurtainsForm__inputMeasurement}>
                    <TextInput
                      aria-label="height in centimeters"
                      id="height"
                      name="height"
                      type="number"
                      placeholderText="e.g. 165.2"
                      labelText="Drop"
                      showLabel={false}
                      required={true}
                      readOnly={false}
                      value={measurementFields.height.value}
                      className={cx(styles.CurtainsForm__inputField, {
                        InputInvalid: measurementFields.height.error,
                      })}
                      min={M2M_CURTAINS_MIN_HEIGHT}
                      max={maxDrop}
                      data-testid="m2m-curtains-input:height"
                      onChange={updateMeasurementFields}
                      onBlur={requestPrice}
                    />
                    <span
                      className={styles.CurtainsForm__unit}
                      aria-hidden="true"
                    >
                      cm
                    </span>
                    {measurementFields.height.valid && (
                      <TickCircleFilled
                        size="large"
                        className={styles.tickIcon}
                      />
                    )}
                    {measurementFields.height.error && (
                      <ErrorFilled size="large" className={styles.errorIcon} />
                    )}
                  </div>
                  {priceError?.type === M2MErrorType.HEIGHT && (
                    <PriceError
                      errorHeading={priceError.heading}
                      errorBody={priceError.body}
                    />
                  )}
                  {measurementFields.height.error && (
                    <div data-testid="m2m-curtains-input-error:height">
                      <span className={styles.CurtainsForm__inputFieldError}>
                        {measurementFields.height.error}
                      </span>
                    </div>
                  )}
                  <p
                    className={styles.CurtainsForm__instruction}
                    data-testid="m2m-curtains-input-description:height"
                  >
                    Minimum drop is {M2M_CURTAINS_MIN_HEIGHT}cm
                  </p>
                </div>
              </div>
            </AccordionItem>

            <AccordionItem
              id="accordion-m2m-curtains-lining-options"
              data-testid="m2m-curtains-accordion:lining-options"
              header={
                <div className={styles.CurtainsForm__accordionTitle}>
                  3. Choose lining options
                </div>
              }
              isOpen={false}
              aria-label="3. Choose lining options"
            >
              <UnderlinedButton
                tag="a"
                type="button"
                onClick={() => toggleHelpModal(helpSections[3])}
                className={styles.CurtainsForm__help_link}
                data-testid="m2m-curtains-help:lining-types"
              >
                Learning about lining types
              </UnderlinedButton>
              <OptionList name="liningWeight" isButtonList={false}>
                <RadioButton
                  id="premium-cotton-lining"
                  label="Premium Cotton Lining (£0)"
                  value={LiningWeight.Standard}
                  checked={
                    radioButtonValues.liningWeight.value ===
                    LiningWeight.Standard
                  }
                  onChange={updateRadioButton}
                  isControlled
                />
                <RadioButton
                  id="thermal-blackout-lining"
                  label={`Premium Thermal Blackout Lining ${blackoutPrice ? `(+${blackoutPrice})` : "(extra cost)"}`}
                  value={LiningWeight.Blackout}
                  checked={
                    radioButtonValues.liningWeight.value ===
                    LiningWeight.Blackout
                  }
                  onChange={updateRadioButton}
                  isControlled
                />
              </OptionList>
              <HorizontalRule />
              <OptionList name="liningColour" isButtonList={false}>
                <div className={styles.OptionsList__radio} key={"cream-lining"}>
                  <img src="https://static.johnlewiscontent.com/v1/static/lining-colour-cream" />
                  <RadioButton
                    id="cream-lining"
                    label="Cream"
                    value="Cream"
                    name="liningColour"
                    checked={radioButtonValues.liningColour.value === "Cream"}
                    onChange={updateRadioButton}
                    isControlled
                  />
                </div>
                <div className={styles.OptionsList__radio} key={"white-lining"}>
                  <img src="https://static.johnlewiscontent.com/v1/static/lining-colour-white" />
                  <RadioButton
                    id="white-lining"
                    label="White"
                    value="White"
                    name="liningColour"
                    checked={radioButtonValues.liningColour.value === "White"}
                    onChange={updateRadioButton}
                    isControlled
                  />
                </div>
              </OptionList>
            </AccordionItem>
          </Accordion>
        </div>

        <div
          className={styles.CurtainsForm__message}
          data-testid="m2m-curtains-measurements-info-message"
        >
          <Message
            title="Please double check your measurements, as we custom make every curtain."
            type="information"
          />
        </div>
        <div
          className={styles.CurtainsForm__summary}
          data-testid="m2m-curtains-form-summary-container"
        >
          <div
            className={styles.CurtainsForm__summaryProduct}
            data-testid="m2m-rcurtains-form-summary-product-container"
          >
            <p data-testid="m2m-curtains-form-summary-product-title">
              {formatM2MTitle(variant.title, false)}
            </p>
            <Price
              price={totalPrice ? { now: totalPrice } : priceFrom}
              pricePrefix={
                totalPrice ? "Total Curtain Price" : "Fabric Price per metre"
              }
              usageContext="pdp"
              showPriceReduction={false}
            />
          </div>
          <div
            className={styles.CurtainsForm__summaryCta}
            data-testid="m2m-curtains-form-summary-cta-container"
          >
            <PrimaryButton
              tag="button"
              type="button"
              disabled={!isReviewButtonEnabled}
              submitting={isPriceCalculating}
              onClick={() => setIsReviewModalOpen(!isReviewModalOpen)}
              data-testid="m2m-curtains-form-summary-cta"
            >
              Review your Curtain order
            </PrimaryButton>
          </div>
        </div>
        {priceError?.type === M2MErrorType.GENERAL && (
          <PriceError
            errorHeading={priceError.heading}
            errorBody={priceError.body}
          />
        )}
      </form>

      <MadeToMeasureHelpModal
        visibleSection={helpModal.visibleSection}
        isOpen={helpModal.open}
        onClose={toggleHelpModal}
        sections={helpSections}
      />

      {isReviewModalOpen && (
        <MadeToMeasureReviewModal
          configuration={getConfigFromFormData()}
          totalPrice={totalPrice}
          product={product}
          runtimeConfig={runtimeConfig}
          flags={flags}
          isOpen
          onClose={() => setIsReviewModalOpen(false)}
          configurationReqType={"MadeToMeasureCurtain"}
        />
      )}
    </div>
  );
};

type EyeletProps = {
  name: string;
  altName: string;
  value: string;
  onChange: (e: ChangeEvent) => void;
};

const CurtainEyelet = ({ name, altName, value, onChange }: EyeletProps) => {
  return (
    <div className={styles.EyeletList__radio}>
      <img
        src={`https://static.johnlewiscontent.com/v1/static/curtain-eyelet-${altName}`}
      />
      <RadioButton
        id={`m2m-eyelet-input-${altName}`}
        label={name}
        value={name}
        name="eyeletColour"
        checked={value === name}
        onChange={onChange}
        isControlled
      />
    </div>
  );
};
